import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import TopBar from "../components/TopBar";
import { useAuth } from "../lib/auth";
import {
  Plus, CheckCircle2, AlertCircle, Circle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { greetingFor } from "../lib/greeting";

const SORT_OPTS = [
  { id: "default", label: "Pending check-ins · name" },
  { id: "name", label: "Client name (A → Z)" },
  { id: "checkins_done", label: "Check-ins done first" },
  { id: "checkins_pending", label: "Check-ins pending first" },
];

function CheckinStatusBadge({ status, kind = "daily" }) {
  if (!status) return null;
  const labelBase = kind === "weekly" ? "weekly" : "daily";
  // No check-in fields configured for this client — show a quiet neutral badge instead of a green tick
  if ((status.total || 0) === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono border border-zinc-800 text-zinc-500" data-testid={`checkin-badge-${labelBase}-none`}>
        <Circle className="w-3 h-3" />
        No {labelBase} check-ins
      </span>
    );
  }
  const allDone = status.all_done;
  const cls = allDone
    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
    : status.completed === 0
      ? "bg-[#EF4444]/15 border-[#EF4444]/40 text-[#FCA5A5]"
      : "bg-amber-500/15 border-amber-500/40 text-amber-300";
  const Icon = allDone ? CheckCircle2 : status.completed === 0 ? AlertCircle : Circle;
  const label = allDone
    ? `${labelBase.charAt(0).toUpperCase()}${labelBase.slice(1)} done`
    : `${status.completed}/${status.total} ${labelBase}`;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-mono border ${cls}`} data-testid={`checkin-badge-${labelBase}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Tri-state color helper: 0 done = red, partial = grey, all done = green.
function progressBadgeCls(done, total) {
  if ((total || 0) === 0) return null;
  if (done >= total) return "bg-emerald-500/15 border-emerald-500/40 text-emerald-300";
  if (done === 0)     return "bg-[#EF4444]/15 border-[#EF4444]/40 text-[#FCA5A5]";
  return "bg-zinc-800/60 border-zinc-700 text-zinc-300";
}

function ClientCard({ row, isAdmin }) {
  const r = row;
  const clientHref = `/clients/${r.client.slug || r.client.id}`;
  const ps = r.payment_summary;
  return (
    <Link to={clientHref} data-testid={`client-row-${r.client.id}`}
      className="group tt-card p-5 block hover:translate-y-[-2px] anim-fade-up">
      <div className="flex items-start gap-3 mb-4">
        {r.client.logo_url ? (
          <img src={r.client.logo_url} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-zinc-900 border border-zinc-800 flex items-center justify-center font-display text-[14px] shrink-0">{r.client.name?.[0]}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display text-[14px] font-semibold text-zinc-100 truncate">{r.client.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{r.client.overview || "No overview"}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <CheckinStatusBadge status={r.checkin_status} kind="daily" />
        <CheckinStatusBadge status={r.checkin_status_weekly} kind="weekly" />
      </div>
      {!r.checkin_status?.all_done && (r.checkin_status?.pending_fields || []).length > 0 && (
        <div className="mt-2 text-[10px] text-zinc-500 font-mono truncate">
          Pending: {r.checkin_status.pending_fields.slice(0, 3).join(" · ")}
          {r.checkin_status.pending_fields.length > 3 ? ` +${r.checkin_status.pending_fields.length - 3}` : ""}
        </div>
      )}
      {isAdmin && ps && (ps.outstanding_total > 0 || ps.next_date) && (
        <div className="mt-3 pt-3 border-t border-zinc-900 flex items-center justify-between text-[11px] font-mono"
             data-testid={`payment-summary-${r.client.id}`}>
          <span className="text-zinc-500">
            {ps.next_date ? (
              <>Next: <span className="text-zinc-200">{new Date(ps.next_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
                {ps.next_amount ? <> · ₹{Number(ps.next_amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</> : null}</>
            ) : <span className="text-zinc-600">No upcoming payment</span>}
          </span>
          {ps.outstanding_total > 0 && (
            <span className="text-amber-400">
              ₹{Number(ps.outstanding_total).toLocaleString("en-IN", { maximumFractionDigits: 0 })} due
            </span>
          )}
        </div>
      )}
      {isAdmin && r.engagement_summary?.status && r.engagement_summary.status !== "ok" && (
        <div className="mt-2 flex items-center justify-between text-[11px] font-mono"
             data-testid={`engagement-summary-${r.client.id}`}>
          <span className={
            r.engagement_summary.status === "ended" ? "text-[#EF4444]" :
            r.engagement_summary.status === "critical" ? "text-[#EF4444]" :
            r.engagement_summary.status === "warn" ? "text-amber-400" : "text-zinc-500"
          }>
            {r.engagement_summary.status === "ended"
              ? `Engagement ended ${Math.abs(r.engagement_summary.days_left)}d ago`
              : `${r.engagement_summary.days_left}d of engagement left`}
          </span>
          {r.engagement_summary.end && (
            <span className="text-zinc-500">until {new Date(r.engagement_summary.end).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
          )}
        </div>
      )}
      {isAdmin && r.engagement_summary?.status === "ok" && r.engagement_summary?.days_left != null && (
        <div className="mt-2 text-[10px] text-zinc-500 font-mono"
             data-testid={`engagement-summary-${r.client.id}`}>
          {r.engagement_summary.days_left}d of engagement left
        </div>
      )}
    </Link>
  );
}

function greeting() {
  return greetingFor("").phrase;
}

export default function MasterDashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [sortBy, setSortBy] = useState("default");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [overview, setOverview] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const m = await api.get("/dashboard/master");
    setRows(m.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Refresh master dashboard when a checkin gets saved anywhere
  useEffect(() => {
    const onCheckin = () => load();
    window.addEventListener("tt-checkin-updated", onCheckin);
    return () => window.removeEventListener("tt-checkin-updated", onCheckin);
  }, []);

  const createClient = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/clients", { name, overview });
      setName(""); setOverview(""); setOpen(false); load();
      toast.success("Client created");
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const sorted = useMemo(() => {
    const cmp = {
      name: (a, b) => a.client.name.localeCompare(b.client.name),
      checkins_done: (a, b) => {
        const ad = a.checkin_status?.all_done ? 1 : 0;
        const bd = b.checkin_status?.all_done ? 1 : 0;
        if (ad !== bd) return bd - ad;
        return a.client.name.localeCompare(b.client.name);
      },
      checkins_pending: (a, b) => {
        const ad = a.checkin_status?.all_done ? 1 : 0;
        const bd = b.checkin_status?.all_done ? 1 : 0;
        if (ad !== bd) return ad - bd;
        return a.client.name.localeCompare(b.client.name);
      },
      default: (a, b) => {
        const ad = a.checkin_status?.all_done ? 1 : 0;
        const bd = b.checkin_status?.all_done ? 1 : 0;
        if (ad !== bd) return ad - bd; // pending first
        return a.client.name.localeCompare(b.client.name);
      },
    };
    return [...rows].sort(cmp[sortBy]);
  }, [rows, sortBy]);

  const firstName = (user?.name || "").split(" ")[0] || "team";
  const { phrase: greetPhrase, full: greetFull } = greetingFor(user?.name);
  const isAdmin = user?.role === "admin";
  const isAM = user?.role === "account_manager";

  // Dynamic subtitle by role
  const pendingCount = rows.filter((r) => !r.checkin_status?.all_done).length;
  const doneCount = rows.length - pendingCount;
  const subtitle = isAM
    ? (rows.length === 0
        ? "No clients assigned to your teams yet."
        : pendingCount === 0
          ? `All ${rows.length} client${rows.length !== 1 ? "s" : ""} have today's check-ins logged. Nice.`
          : `${pendingCount} of ${rows.length} client${rows.length !== 1 ? "s" : ""} still need today's check-ins.`)
    : `${rows.length} active workspace${rows.length !== 1 ? "s" : ""}${isAdmin && doneCount > 0 ? ` · ${doneCount} caught up on check-ins` : ""}.`;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <TopBar />
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 anim-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-5 mb-7">
          <div>
            <div className="t-eyebrow mb-2">Home · {greetPhrase}</div>
            <h1 className="h-display-1" data-testid="dashboard-title">{greetFull}</h1>
            <p className="text-zinc-500 mt-1.5 text-[13px]" data-testid="dashboard-subtitle">{subtitle}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#111] border border-zinc-800 rounded-md px-3 h-9 text-[12px] focus:outline-none focus:border-zinc-500 max-w-[220px] truncate"
              data-testid="dashboard-sort">
              {SORT_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            {isAdmin && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-white hover:bg-zinc-200 text-black h-9 text-[12px]" data-testid="new-client-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> New client</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#111] border-zinc-800">
                  <DialogHeader><DialogTitle className="font-display">Create new client</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Client name</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0c0c0e] border-zinc-800 mt-1.5 text-[13px]" data-testid="new-client-name" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Overview (optional)</Label>
                      <Textarea value={overview} onChange={(e) => setOverview(e.target.value)} className="bg-[#0c0c0e] border-zinc-800 mt-1.5 text-[13px]" data-testid="new-client-overview" />
                    </div>
                    {err && <div className="text-[12px] text-[#EF4444]">{err}</div>}
                  </div>
                  <DialogFooter>
                    <Button onClick={createClient} disabled={busy || !name} className="bg-white hover:bg-zinc-200 text-black" data-testid="new-client-submit">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Clients grid — simplified to check-in status only */}
        {loading ? (
          <div className="text-center text-zinc-600 text-[12px] py-12">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-zinc-600 text-[12px] py-12">No clients yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="master-table">
            {sorted.map((r) => <ClientCard key={r.client.id} row={r} isAdmin={user?.role === "admin"} />)}
          </div>
        )}
      </div>
    </div>
  );
}
